import { db } from '../firebase';
import { collection, doc, getDoc, setDoc, query, where, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { sendEmailNotification, EMAIL_TEMPLATES } from './notifications';
import { notifyMemberJoined } from './notificationService';
import logger from '../utils/logger';

const COLLECTION_NAME = 'invites';

const inviteService = {
  createInvite: async (inviterId, recipientEmail, familyCode, relationship = null, recipientName = null) => {
    try {
      // Debug logging for authentication state
      const auth = getAuth();
      if (!auth) {
        throw new Error('Firebase Auth is not initialized');
      }
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      // Validate inputs
      if (!inviterId) throw new Error('Inviter ID is required');
      if (!recipientEmail) throw new Error('Recipient email is required');
      if (!familyCode) throw new Error('Family code is required');

      // Verify the inviterId matches the current user
      if (inviterId !== currentUser?.uid) {
        throw new Error('Invalid inviter ID');
      }

      // Get inviter's details first to verify permissions
      const inviterRef = doc(db, 'users', inviterId);
      const inviterDoc = await getDoc(inviterRef);
      
      if (!inviterDoc?.exists?.()) {
        logger.error('Inviter not found:', inviterId);
        throw new Error('Inviter not found');
      }

      const inviterData = inviterDoc.data();
      
      // Verify inviter has a family code
      if (!inviterData?.familyCode) {
        logger.error('Inviter has no family code:', inviterId);
        throw new Error('You must be part of a family to send invites');
      }

      // Verify family code matches
      if (inviterData.familyCode !== familyCode) {
        logger.error('Family code mismatch:', { userCode: inviterData.familyCode, providedCode: familyCode });
        throw new Error('Invalid family code');
      }

      // Create the invite record
      const invitesRef = collection(db, COLLECTION_NAME);
      const newInviteRef = doc(invitesRef);
      
      const inviteData = {
        inviterId,
        recipientEmail,
        familyCode,
        status: 'pending',
        createdAt: new Date(),
        inviterEmail: inviterData.email || currentUser.email,
        inviterName: inviterData.firstName && inviterData.lastName
          ? `${inviterData.firstName} ${inviterData.lastName}`
          : currentUser.displayName || 'Family Member',
        relationship: relationship || null, // Relationship from inviter to invitee
        recipientName: recipientName || null // Optional recipient name
      };

      await setDoc(newInviteRef, inviteData);

      // Send email notification
      await sendEmailNotification(EMAIL_TEMPLATES.PENDING_APPROVAL, {
        to: recipientEmail,
        inviterName: `${inviterData.firstName} ${inviterData.lastName}`,
        familyCode: familyCode,
        inviteId: newInviteRef.id,
        acceptUrl: `${window.location.origin}/accept-invite/${newInviteRef.id}`
      });
      
      return newInviteRef.id;
    } catch (error) {
      logger.error('Error creating invite:', error);
      // Log additional error details if available
      if (error.code) {
        logger.error('Error code:', error.code);
      }
      if (error.details) {
        logger.error('Error details:', error.details);
      }
      throw error; // Re-throw the error to be handled by the component
    }
  },

  getInvitesByEmail: async (email) => {
    try {
      // Check authentication
      const auth = getAuth();
      if (!auth) {
        logger.error('Firebase Auth is not initialized');
        throw new Error('Firebase Auth is not initialized');
      }
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        logger.error('No authenticated user found');
        throw new Error('No authenticated user found');
      }

      // Try to get user document to check role
      let userDoc = null;
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        userDoc = await getDoc(userRef);
      } catch (userError) {
        logger.warn('Error fetching user document:', userError);
      }

      // Query invites collection
      const invitesRef = collection(db, COLLECTION_NAME);
      
      // Build query based on user's role and email
      let queryConstraints = [];
      
      // If user is looking up their own invites
      if (email === currentUser.email) {
        queryConstraints.push(where('recipientEmail', '==', email));
      } 
      // If user is owner/admin, they can see all invites
      else if (userDoc?.exists() && ['owner', 'admin'].includes(userDoc.data().role)) {
        if (email) {
          queryConstraints.push(where('recipientEmail', '==', email));
        }
      } 
      // If user is looking up invites they created
      else {
        queryConstraints.push(where('inviterId', '==', currentUser.uid));
        if (email) {
          queryConstraints.push(where('recipientEmail', '==', email));
        }
      }

      const q = query(invitesRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return [];
      }
      
      const invites = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return invites;
    } catch (error) {
      logger.error('Error fetching invites:', error);
      throw error;
    }
  },

  acceptInvite: async (inviteId) => {
    try {
      const inviteRef = doc(db, COLLECTION_NAME, inviteId);
      const inviteDoc = await getDoc(inviteRef);

      if (!inviteDoc.exists()) {
        throw new Error('Invite not found');
      }

      const inviteData = inviteDoc.data();

      // Get the current authenticated user (the person accepting the invite)
      const auth = getAuth();
      const currentUser = auth?.currentUser;

      await updateDoc(inviteRef, {
        status: 'accepted',
        acceptedAt: new Date()
      });

      // Notify the inviter that their invite was accepted
      const inviterRef = doc(db, 'users', inviteData.inviterId);
      const inviterDoc = await getDoc(inviterRef);

      if (inviterDoc.exists()) {
        await sendEmailNotification(EMAIL_TEMPLATES.APPROVAL_GRANTED, {
          to: inviterDoc.data().email,
          recipientEmail: inviteData.recipientEmail,
          familyCode: inviteData.familyCode
        });
      }

      // Send in-app notification to all family members that a new member joined
      if (currentUser) {
        try {
          // Get the new member's profile data
          const newMemberRef = doc(db, 'users', currentUser.uid);
          const newMemberDoc = await getDoc(newMemberRef);

          if (newMemberDoc.exists()) {
            const newMemberData = newMemberDoc.data();
            const newMemberName = newMemberData.firstName && newMemberData.lastName
              ? `${newMemberData.firstName} ${newMemberData.lastName}`
              : newMemberData.displayName || inviteData.recipientEmail;

            // Notify all family members (except the new member) that someone joined
            await notifyMemberJoined(
              inviteData.familyCode,
              newMemberName,
              currentUser.uid
            );

          }
        } catch (notificationError) {
          // Log error but don't fail the invite acceptance
          logger.error('Error sending member joined notification:', notificationError);
        }
      }
    } catch (error) {
      logger.error('Error accepting invite:', error);
      throw error;
    }
  },

  declineInvite: async (inviteId) => {
    try {
      const inviteRef = doc(db, COLLECTION_NAME, inviteId);
      const inviteDoc = await getDoc(inviteRef);
      
      if (!inviteDoc.exists()) {
        throw new Error('Invite not found');
      }
      
      const inviteData = inviteDoc.data();
      
      await updateDoc(inviteRef, {
        status: 'declined',
        declinedAt: new Date()
      });

      // Notify the inviter that their invite was declined
      const inviterRef = doc(db, 'users', inviteData.inviterId);
      const inviterDoc = await getDoc(inviterRef);
      
      if (inviterDoc.exists()) {
        await sendEmailNotification('invite-declined', {
          to: inviterDoc.data().email,
          recipientEmail: inviteData.recipientEmail,
          familyCode: inviteData.familyCode
        });
      }
    } catch (error) {
      logger.error('Error declining invite:', error);
      throw error;
    }
  },

  cancelInvite: async (inviteId) => {
    try {
      const inviteRef = doc(db, COLLECTION_NAME, inviteId);
      const inviteDoc = await getDoc(inviteRef);
      
      if (!inviteDoc.exists()) {
        throw new Error('Invite not found');
      }
      
      const inviteData = inviteDoc.data();
      
      await deleteDoc(inviteRef);

      // Notify the recipient that the invite was cancelled
      await sendEmailNotification('invite-cancelled', {
        to: inviteData.recipientEmail,
        familyCode: inviteData.familyCode
      });
    } catch (error) {
      logger.error('Error canceling invite:', error);
      throw error;
    }
  },

  sendInviteEmail: async (recipientEmail, inviteCode) => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to send invites');
      }

      const functions = getFunctions();
      const sendEmail = httpsCallable(functions, 'sendEmail');

      const result = await sendEmail({
        templateId: EMAIL_TEMPLATES.PENDING_APPROVAL,
        to: recipientEmail,
        dynamicData: {
          inviteCode,
          inviteUrl: `${window.location.origin}/join?code=${inviteCode}`
        }
      });

      if (!result.data.success) {
        throw new Error('Failed to send invite email');
      }

      return true;
    } catch (error) {
      logger.error('Error sending invite email:', error);
      throw error;
    }
  }
};

export default inviteService;